import React, { FC } from 'react';
import { TFunction } from 'next-i18next';
import type { OptimizedBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import { PATHS } from 'src/common/constants';
import { CarouselProvider } from 'src/common/providers/CarouselProvider';
import CarouselButtons from 'src/common/components/dockyard/CarouselButtons';
import Carousel from 'src/common/components/dockyard/Carousel';
import ListingCard from 'src/common/components/ListingCard';
import Typography from 'src/common/components/Typography';
import PitchPageLayout from 'src/common/components/PitchPageLayout';
import Button from 'src/common/components/ButtonDS22';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import styles from './LandingPageListingsByLocation.module.scss';

type ListingLocation = {
  ctaSlug: string;
  ctaText: string;
  listings: OptimizedBoatSearchDetail[];
  title: string;
};

export type LandingPageListingsByLocationProps = {
  listingLocations: ListingLocation[];
  translationFunction?: TFunction;
};

const LandingPageListingsByLocation: FC<LandingPageListingsByLocationProps> = ({
  listingLocations,
  translationFunction,
}) => {
  const { isMobileNativeWebView } = useMobileNativeWebView();

  return (
    <PitchPageLayout>
      <div className={styles.root}>
        {listingLocations.map((location) => (
          <div key={location.title} className={styles.section}>
            <CarouselProvider>
              <div className={styles.header}>
                <Typography
                  type="h2"
                  size="display-5"
                  weight="semibold"
                >
                  {translationFunction ? translationFunction(location.title) : location.title}
                </Typography>
                <div className={styles.buttonsDesktop}>
                  <CarouselButtons
                    spacing="together"
                    variant="primary-dark"
                  />
                </div>
              </div>
              <div className={styles.carousel}>
                <Carousel>
                  {location.listings.map((listing) => (
                    <ListingCard
                      key={listing.id}
                      listingId={listing.id}
                      listingUrl={listing.listing_url}
                      headline={
                        translationFunction
                          ? translationFunction(listing.headline)
                          : listing.headline
                      }
                      breadcrumbs={listing.breadcrumbs}
                      category={listing.cat_loc_breadcrumb}
                      rating={
                        listing.review_average
                          ? parseFloat(listing.review_average)
                          : undefined
                      }
                      awards={listing.user_details.awards}
                      isSuperOwner={listing.user_details.is_super_owner}
                      instantBookingAvailable={listing.has_available_instabook}
                      searchPricing={listing.search_price_nested}
                      imageSrc={listing.photo_url ?? ''}
                      capacity={translationFunction ? translationFunction('listings-capacity', { capacity: listing.capacity }) : `${listing.capacity} guests`} // 'guests' is to support storybook
                      withFavorite={!isMobileNativeWebView}
                      loading="lazy"
                    />
                  ))}
                </Carousel>
              </div>
              <div className={styles.buttonsMobile}>
                <CarouselButtons
                  spacing="together"
                  variant="primary-dark"
                />
              </div>
            </CarouselProvider>
            <Button
              type="link"
              href={`${PATHS.SEARCH}${location.ctaSlug}`}
              variant="tertiary"
              fitWidth="m"
            >
              {translationFunction ? translationFunction(location.ctaText) : location.ctaText}
            </Button>
          </div>
        ))}
      </div>
    </PitchPageLayout>
  );
};

export default LandingPageListingsByLocation;
